/**
 * Estado de interfaz (no colaborativo): herramienta activa, estilos elegidos,
 * cámara, selección y edición de texto. Emite eventos para que la UI se redibuje.
 */
import type { GeoKind, ShapeId } from '../../shared/types.ts'
import { clamp } from '../lib/geom.ts'

export type ToolId =
  | 'select'
  | 'hand'
  | 'draw'
  | 'highlighter'
  | 'eraser'
  | 'note'
  | 'text'
  | 'shape'
  | 'image'
  | 'laser'

export interface Camera {
  /** coordenada de mundo que aparece en la esquina superior izquierda de la pantalla */
  x: number
  y: number
  /** factor de zoom (1 = 100 %) */
  z: number
}

/** Cuánto responde el grosor a la presión del Apple Pencil. */
export type PenPressure = 'off' | 'low' | 'normal' | 'high'

export const PRESSURE_THINNING: Record<PenPressure, number> = {
  off: 0,
  low: 0.3,
  normal: 0.55,
  high: 0.8,
}

export const PRESSURE_LABELS: Record<PenPressure, string> = {
  off: 'Parejo',
  low: 'Suave',
  normal: 'Normal',
  high: 'Marcada',
}

export interface StyleState {
  inkColor: string
  inkSize: number
  /** sensibilidad a la presión de la pluma */
  penPressure: PenPressure
  highlighterColor: string
  highlighterSize: number
  noteColor: string
  shapeKind: GeoKind
  shapeColor: string
  shapeFill: string
  shapeStrokeWidth: number
  textColor: string
  textSize: number
  eraserMode: 'stroke' | 'area'
  eraserSize: number
}

const STYLE_KEY = 'wb.style.v1'
const PREFS_KEY = 'wb.prefs.v1'
const TOOL_KEY = 'wb.tool.v1'

/** Herramientas que tiene sentido restaurar al recargar (el láser no). */
const RESTORABLE_TOOLS = ['select', 'hand', 'draw', 'highlighter', 'eraser', 'note', 'text', 'shape'] as const

/** ¿Corremos dentro de la app nativa de iPad? (sin importar el puente: evita un ciclo de módulos) */
function isNativeIpadShell(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.webkit?.messageHandlers?.pizarra
  } catch {
    return false
  }
}

export const PREF_KEYS = [
  'enhanceShapes',
  'showCursors',
  'wheelZooms',
  'touchPans',
  'snapToGrid',
  'palmRejection',
  'toolbarTop',
  'penAlwaysInks',
  'darkMode',
] as const
export type PrefKey = (typeof PREF_KEYS)[number]

const DEFAULT_STYLE: StyleState = {
  inkColor: '#1A1A1A',
  inkSize: 4,
  penPressure: 'normal',
  highlighterColor: '#FFF100',
  highlighterSize: 20,
  noteColor: '#FEF3A3',
  shapeKind: 'rectangle',
  shapeColor: '#1A1A1A',
  shapeFill: 'none',
  shapeStrokeWidth: 3,
  textColor: '#1A1A1A',
  textSize: 28,
  eraserMode: 'stroke',
  eraserSize: 28,
}

export const MIN_ZOOM = 0.1
export const MAX_ZOOM = 8

type Listener = () => void

export class Store {
  tool: ToolId = 'select'
  /** herramienta a la que volver tras un uso puntual (p. ej. tras pegar una imagen) */
  previousTool: ToolId = 'select'
  camera: Camera = { x: -600, y: -400, z: 1 }
  selection: ShapeId[] = []
  editingId: ShapeId | null = null
  hoveredId: ShapeId | null = null
  style: StyleState = { ...DEFAULT_STYLE }
  snapToGrid = false
  followingUser: string | null = null
  userName = ''
  readOnly = false
  /** convertir trazos reconocibles en figuras limpias (Alt+B / ajuste) */
  enhanceShapes = false
  /** mostrar cursores de los colaboradores */
  showCursors = true
  /** la rueda del ratón hace zoom (comportamiento de Microsoft Whiteboard) */
  wheelZooms = true
  /** con pluma activa, un dedo desplaza el lienzo en vez de dibujar */
  touchPans = true
  /**
   * Rechazo de palma: el dedo solo navega (desplaza y hace zoom), NUNCA edita —
   * sin importar la herramienta activa. Antes esto dependía de que hubiera una
   * herramienta de tinta activa, y como la app vuelve sola a "seleccionar" tras
   * casi cualquier acción, la mano apoyada abría un marco y seleccionaba todo.
   */
  palmRejection = true
  /** La barra de herramientas va arriba (bajo el título) en vez de abajo. */
  toolbarTop = false
  /**
   * El lápiz siempre escribe sobre lienzo vacío, aunque la herramienta activa
   * sea la de seleccionar (como en Freeform). Sobre una figura sigue
   * seleccionando y moviendo. Por defecto solo en el iPad: en escritorio una
   * tableta gráfica se usa como ratón.
   */
  penAlwaysInks = isNativeIpadShell()
  /** Última pluma usada: a esta vuelve el lápiz cuando escribe desde "seleccionar". */
  lastInkTool: 'draw' | 'highlighter' = 'draw'
  /**
   * Modo oscuro (solo para este usuario): lienzo e interfaz en negro y la tinta
   * oscura se exhibe clara. No cambia los datos del tablero: los demás lo ven
   * con sus propios colores.
   */
  darkMode = false

  private listeners = new Set<Listener>()

  constructor() {
    try {
      const raw = localStorage.getItem(STYLE_KEY)
      if (raw) this.style = { ...DEFAULT_STYLE, ...(JSON.parse(raw) as Partial<StyleState>) }
      const prefs = localStorage.getItem(PREFS_KEY)
      if (prefs) {
        const p = JSON.parse(prefs) as Partial<Record<PrefKey, boolean>>
        for (const key of PREF_KEYS) if (typeof p[key] === 'boolean') this[key] = p[key]
      }
      // La herramienta sobrevive a la recarga: si el iPad arrancara siempre en
      // "seleccionar", el lápiz señalaría en vez de escribir tras cada apertura.
      const tool = localStorage.getItem(TOOL_KEY) as ToolId | null
      if (tool && (RESTORABLE_TOOLS as readonly string[]).includes(tool)) {
        this.tool = tool
        if (tool === 'draw' || tool === 'highlighter') this.lastInkTool = tool
      }
    } catch {
      /* estilos por defecto */
    }
  }

  setPref(key: PrefKey, value: boolean): void {
    this[key] = value
    try {
      const current: Record<string, boolean> = {}
      for (const k of PREF_KEYS) current[k] = this[k]
      localStorage.setItem(PREFS_KEY, JSON.stringify(current))
    } catch {
      /* modo privado */
    }
    this.emit()
  }

  subscribe(cb: Listener): () => void {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  emit(): void {
    for (const cb of this.listeners) cb()
  }

  setTool(tool: ToolId): void {
    if (this.tool === tool) return
    this.previousTool = this.tool
    this.tool = tool
    if (tool === 'draw' || tool === 'highlighter') this.lastInkTool = tool
    if (tool !== 'select') {
      this.selection = []
      this.editingId = null
    }
    try {
      if ((RESTORABLE_TOOLS as readonly string[]).includes(tool)) localStorage.setItem(TOOL_KEY, tool)
    } catch {
      /* modo privado */
    }
    this.emit()
  }

  setStyle(patch: Partial<StyleState>): void {
    this.style = { ...this.style, ...patch }
    try {
      localStorage.setItem(STYLE_KEY, JSON.stringify(this.style))
    } catch {
      /* modo privado: seguimos sin persistir */
    }
    this.emit()
  }

  setSelection(ids: ShapeId[]): void {
    const same = ids.length === this.selection.length && ids.every((id, i) => this.selection[i] === id)
    if (same) return
    this.selection = ids
    if (this.editingId && !ids.includes(this.editingId)) this.editingId = null
    this.emit()
  }

  setCamera(camera: Partial<Camera>): void {
    const next = { ...this.camera, ...camera }
    next.z = clamp(next.z, MIN_ZOOM, MAX_ZOOM)
    this.camera = next
    this.emit()
  }

  setEditing(id: ShapeId | null): void {
    if (this.editingId === id) return
    this.editingId = id
    this.emit()
  }
}
